// Single source of truth for what a margin number MEANS in this codebase.
//
// One convention, everywhere:
//
//   gross_margin_rate g, in [0, 1)      price = cost / (1 - g)
//
// g = 0    -> price == cost (zero margin; this is how most shipping lanes run)
// g = 0.15 -> price ~= 1.18x cost
// g = 0.50 -> price == 2x cost
//
// Historically two conventions coexisted. The other one stored a COST RATIO m
// with price = cost / m and gross margin = 1 - m. They are inverses, and they
// agree at exactly one value:
//
//   cost / (1 - m) == cost / m   <=>   m = 0.5
//
// which is why the mismatch survived for so long - 0.5 was the most commonly
// configured value, and it is the one place the two readings coincide. Stored
// 0.35 meant 35% margin under one reading and 65% under the other.
//
// Mirrored in netlify/functions/utils/marginResolution.js. Keep in sync.

// Price from cost at a target gross margin rate.
//
// A rate at or above 1 has no price - it asks for cost / 0. Until 2026-09-16
// this returned the COST in that case, which is the worst available answer:
// a line billed at exactly cost looks entirely normal on a quote, so a 100%
// entry read as a pass-through rather than as the impossible thing it is.
// NaN cannot be mistaken for a price.
//
// Every gross_margin_rate column carries a CHECK constraint pinning it to
// [0, 1), so this is a backstop for DERIVED rates - chiefly
// ingredient_categories.default_margin, which carries no such constraint.
export function priceFromGrossMarginRate(cost, rate) {
  const c = Number(cost) || 0;
  const g = Number(rate) || 0;
  if (!(g >= 0) || g >= 1) return NaN;
  return c / (1 - g);
}

// The gross margin rate a cost/price pair implies. Inverse of the above.
export function grossMarginRateFromPrice(cost, price) {
  const c = Number(cost) || 0;
  const p = Number(price) || 0;
  return p > 0 ? (p - c) / p : 0;
}

// Convert a legacy cost-ratio `margin` to a gross margin rate.
//
// Use this ONLY when reading data written before the normalization - chiefly
// quote_add_ons.margin and quote_line_items.margin, which are historical
// records of what a past quote charged and are deliberately not migrated.
// Never use it on ingredients/services; those columns now hold the rate.
export function grossMarginRateFromLegacyMargin(legacyMargin) {
  const m = Number(legacyMargin);
  if (!Number.isFinite(m)) return null;
  return 1 - m;
}

// Resolve the rate to apply to an entity: its own value wins, then its
// category default, then the hard-coded fallback.
//
// Note the `!= null` checks. 0 is a legal gross margin rate (a pass-through
// line), so truthiness would silently fall through to the default and mark up
// something that was configured to carry no margin.
export function resolveGrossMarginRate(entity, categoryRate, fallback = 0) {
  if (entity && entity.gross_margin_rate != null) return Number(entity.gross_margin_rate);
  if (categoryRate != null) return Number(categoryRate);
  return Number(fallback) || 0;
}

// Inverse of grossMarginRateFromLegacyMargin.
//
// TRANSITIONAL. The legacy `margin` columns on ingredients/services are still
// NOT NULL and still read by any older deploy, so every write path populates
// both until the drop migration lands. Delete this and its call sites in the
// same PR that drops the columns.
export function legacyMarginFromGrossMarginRate(rate) {
  const g = Number(rate);
  if (!Number.isFinite(g)) return null;
  return Math.round((1 - g) * 10000) / 10000;
}
